// Auth store — Zustand. Persist JWT vào localStorage để keep login giữa reload.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'cnvwork-saas-auth';

// So sánh sâu, KHÔNG phụ thuộc thứ tự khoá: `module_seats` do BE dựng bằng Promise.all nên thứ tự
// khoá đổi theo lượt trả lời — so bằng JSON.stringify là "đổi" giả, đổi identity mỗi lần refresh.
function sameValue(a, b) {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) return a.length === b.length && a.every((v, i) => sameValue(v, b[i]));
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length
    && ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && sameValue(a[k], b[k]));
}

/**
 * Gộp `user` từ một phản hồi của máy chủ vào `user` phiên đang giữ.
 *
 * ⛔ GỘP, KHÔNG THAY. Mỗi đường trả một TẬP CON khác nhau của `user` (refresh gọn hơn login, login
 * gọn hơn /auth/me). Trước 22/09/2026 setSession THAY nguyên object bằng bản của refresh — mà phép so
 * "cùng user" luôn trượt (`avatar` null ≠ undefined) — nên cứ mỗi lần access token hết hạn (~15 phút)
 * phiên rơi mất mọi trường refresh không trả: người chưa có chỗ Hộp thư thấy Hộp thư như thường, gõ
 * trả lời thì 402, F5 mới hiện màn khoá (tuanpv@cnv.vn báo); softphone CNV Call tự huỷ đăng ký; màn
 * khoá theo gói biến mất; ảnh đại diện + tên theo workspace rơi về mặc định.
 *
 * Chỉ gộp khi CÙNG người + CÙNG workspace. Khác một trong hai là phiên KHÁC ⇒ thay hẳn, không để
 * trường của người / workspace cũ sống sót sang (vd chỗ ngồi Hộp thư của workspace A lọt sang B).
 *
 * Không trường nào đổi ⇒ trả lại ĐÚNG object cũ: giữ identity để effect `[user]` khỏi chạy lại mỗi
 * lần refresh (sự cố burst 2026-05-27: WebPushRegister subscribe 40+ lần).
 */
export function mergeSessionUser(cur, incoming, { sameWorkspace = true } = {}) {
  if (!incoming) return cur ?? null;
  if (!cur || cur.id !== incoming.id || !sameWorkspace) return incoming;
  const changed = Object.keys(incoming).some((k) => !sameValue(cur[k], incoming[k]));
  return changed ? { ...cur, ...incoming } : cur;
}

/** Hai bản activeTenant có cùng workspace không. Thiếu một bên = không kết luận được ⇒ coi là cùng. */
export function isSameWorkspace(curTenant, nextTenant) {
  if (!curTenant?.tenant_id || !nextTenant?.tenant_id) return true;
  return curTenant.tenant_id === nextTenant.tenant_id;
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,                                    // { id, email, display_name }
      activeTenant: null,                            // { tenant_id, org_id, slug, display_name }

      isAuthenticated: () => !!get().accessToken,

      // Fallback to existing nếu field undefined — refresh response chỉ trả {accessToken, user},
      // KHÔNG có refreshToken/active_tenant. Nếu set null/undefined sẽ xóa activeTenant →
      // menu filter mất team_chat (private_modules empty) — đây là bug từng gặp 2026-05-24.
      //
      // `user` đi qua mergeSessionUser: GỘP vào bản đang giữ (refresh chỉ trả một phần), và giữ
      // nguyên identity khi không có gì đổi (2026-05-27 fix burst — identity flip mỗi lần refresh
      // làm WebPushRegister/TeamChatPushNotifications subscribe loop 40+ lần).
      setSession: ({ accessToken, refreshToken, user, active_tenant }) => {
        const cur = get();
        const sameTenant = active_tenant && cur.activeTenant
          && cur.activeTenant.tenant_id === active_tenant.tenant_id
          && JSON.stringify(cur.activeTenant.private_modules || {}) === JSON.stringify(active_tenant.private_modules || {});
        set({
          accessToken,
          refreshToken: refreshToken ?? cur.refreshToken,
          user: mergeSessionUser(cur.user, user, {
            sameWorkspace: isSameWorkspace(cur.activeTenant, active_tenant),
          }),
          activeTenant: active_tenant ? (sameTenant ? cur.activeTenant : active_tenant) : cur.activeTenant,
        });
      },

      // Ghi phản hồi POST /auth/me/switch-tenant — MỘT CỬA cho hai nút đổi workspace (thanh trên
      // cùng WorkspaceSwitcherV2 · màn /select-workspace). Một lần `set`: token, workspace và `user`
      // đổi cùng nhau, không có nhịp render nào token của workspace B đi với quyền của workspace A.
      //
      // `user` GỘP dù khác workspace (mergeSessionUser mặc định): switch-tenant trả phần phiên mà
      // BE đã tính lại cho workspace MỚI — tên/ảnh ở workspace đó, vai trò + quyền, bản quyền tính
      // năng — đè lên là đúng. Trường nó không trả (preferences, hồ sơ nhân sự…) giữ tạm tới khi
      // /auth/me chạy ở lần TẢI LẠI TRANG ngay sau đó — người gọi PHẢI tải lại (cache react-query /
      // reference cache vẫn là của workspace cũ, xem @cnv/ui/layout/WorkspaceSwitcherV2 ghi chú 3).
      // Thay hẳn thì phiên rơi mất `preferences` — trạng thái phiên chưa từng có.
      //
      // refreshToken: BE rotate refresh token khi đổi workspace (refresh claim mang
      // active_tenant_id). Vắng field này (token legacy / mất CAS) → giữ token hiện tại.
      applySwitchTenant: (res) => {
        const cur = get();
        set({
          activeTenant: res?.active_tenant ?? cur.activeTenant,
          accessToken: res?.accessToken ?? cur.accessToken,
          refreshToken: res?.refreshToken ?? cur.refreshToken,
          user: mergeSessionUser(cur.user, res?.user),
        });
      },

      logout: () => set({
        accessToken: null, refreshToken: null, user: null, activeTenant: null,
      }),
    }),
    { name: STORAGE_KEY },
  ),
);

// ─── Sync token giữa các tab (2026-07-25) ────────────────────────────────────────────────
// zustand persist chỉ đọc localStorage LÚC LOAD, không theo dõi thay đổi ⇒ mỗi tab giữ một bản
// token riêng trong bộ nhớ. Với refresh token sliding (BE rotate mỗi lần refresh), tab nền bị
// trình duyệt throttle có thể nằm im rất lâu rồi bắn request bằng token đã lỗi thời → 401 →
// đăng xuất. Nghe `storage` để mọi tab luôn dùng token mới nhất.
//
// ⚠️ WORKSPACE PHẢI ĐI KÈM TOKEN. Access token chính là nguồn tenant ở BE
// (`api/src/middleware/inboxAuth.js`: `req.tenant = claims.active_tenant_id`), FE không gửi
// header tenant nào. Nếu chỉ nuốt token mà giữ nguyên `activeTenant` cũ thì tab này sẽ hiển thị
// workspace Y trong khi MỌI request đọc/ghi chạy trên workspace X — tạo record nhầm workspace.
// Vì vậy: token mang tenant KHÁC tenant tab này đang mở → BỎ QUA hẳn, không nhận token đó.
// (Tab kia đổi workspace là việc của tab kia; tab này giữ nguyên phiên của nó.)
//
// CHỈ xử lý khi có giá trị mới — BỎ QUA sự kiện xoá key, để một tab gặp sự cố không kéo theo
// mọi tab khác cùng văng ra login.
function _tenantOf(token) {
  try {
    const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return p?.active_tenant_id ?? null;
  } catch { return null; }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const next = JSON.parse(e.newValue)?.state;
      if (!next?.accessToken) return;
      const cur = useAuthStore.getState();
      if (next.accessToken === cur.accessToken && next.refreshToken === cur.refreshToken) return;
      const nextTenant = _tenantOf(next.accessToken);
      const curTenant = cur.activeTenant?.tenant_id ?? null;
      if (curTenant && nextTenant && nextTenant !== curTenant) return;   // tab kia đổi workspace
      useAuthStore.setState({
        accessToken: next.accessToken,
        refreshToken: next.refreshToken ?? cur.refreshToken,
      });
    } catch { /* JSON hỏng — bỏ qua, tab này vẫn chạy bằng state hiện tại */ }
  });
}
